import React, {useState, useEffect, forwardRef, useImperativeHandle} from 'react';
import {InputText} from "primereact/inputtext";
import {Password} from "primereact/password";
import {Checkbox} from "primereact/checkbox";
import {Dropdown} from "primereact/dropdown";
import {Message} from "primereact/message";
import {Button} from "primereact/button";

import {MarketplaceProductService} from "../../service/MarketplaceProductService";
import {isAdmin} from "../../utils/Utils";
import {AMAZON_OAUTH_RETURN_HASH_KEY} from "../../utils/amazonOAuth";
import "./MarketplaceConnectStep.css";

const marketplaceProductService = new MarketplaceProductService();

// Marketplace choice now lives in this step (moved out of the wizard's old
// Data source dropdown). Labels are brand names, not translated. `color` and
// `initial` back the logo-card fallback badge (see MarketplaceCard) used
// until real logo files exist at /img/marketplaces/<key>.png.
export const MARKETPLACES = [
    {key: 'trendyol', label: 'Trendyol', color: '#f27a1a', initial: 'T'},
    {key: 'hepsiburada', label: 'Hepsiburada', color: '#ff6000', initial: 'H'},
    {key: 'amazon', label: 'Amazon', color: '#ff9900', initial: 'A'},
];

// Credential fields per marketplace, in display order. `key` is also the
// property name sent as part of `credentials` to marketplace_connect/.
// `defaultValue` prefills a fresh (never-connected) form -- see
// valuesFromStored below; it's ignored once a stored value exists.
const FIELDS = {
    trendyol: [
        {key: 'merchant_id', secret: false},
        {key: 'username', secret: false},
        {key: 'password', secret: true},
        // Required store label for the owner ("You") row. Trendyol's API never
        // returns the seller's own store name, so it's typed here; enforced both
        // client-side (save) and server-side (CREDENTIAL_FIELDS).
        {key: 'merchant_name', secret: false},
    ],
    hepsiburada: [
        {key: 'merchant_id', secret: false},
        {key: 'password', secret: true},
        {key: 'merchant_name', secret: false},
        {key: 'test_environment', type: 'checkbox'},
    ],
    // Self-service since the app was published: a normal user never types
    // seller_id/refresh_token at all -- they pick a country and click
    // "Connect with Amazon" (rendered below the fields; see
    // connectWithAmazon), which sends the browser through Amazon's own
    // consent page and lands the credentials here via the OAuth callback
    // instead. seller_id/refresh_token stay in this list (so an admin's
    // stored values still prefill via valuesFromStored) but are filtered out
    // of the RENDERED fields for everyone else -- see ADMIN_ONLY_FIELD_KEYS
    // below, which keeps a manual fallback for support to connect an account
    // without the seller present. marketplace_id is a customer-selectable
    // country dropdown (`type: 'select'`), whose options come from
    // `optionsKey` (see amazonMarketplaces state, populated from GET
    // marketplace_integration -- the backend's single source of truth for
    // the list is product/marketplace_api/marketplace_countries.py).
    // Defaults to Turkey / amazon.com.tr (A33AVAJ2PDY3EV) when nothing is
    // stored yet -- server-side, connect() can instead default it from the
    // seller's own marketplace participations when that call resolves to
    // exactly one (service_marketplace_campaign.py connect()/
    // _refresh_amazon_store_identity), and rejects an explicitly chosen
    // country the seller doesn't actually sell in. merchant_name is optional
    // and admin-only, like the credential pair: connect() fills it in
    // automatically from the seller's Amazon store name after the first
    // successful connect, and the connected card already shows the result, so
    // a seller has no reason to see a field for it --
    // unlike Hepsiburada, Amazon identifies our own offer by SellerId, so no
    // display label is required to recognize it (CREDENTIAL_FIELDS /
    // SAFE_CREDENTIAL_FIELDS in service_marketplace_campaign.py).
    amazon: [
        {key: 'seller_id', secret: false},
        {key: 'refresh_token', secret: true},
        {key: 'marketplace_id', secret: false, type: 'select', optionsKey: 'amazonMarketplaces',
         defaultValue: 'A33AVAJ2PDY3EV'},
        {key: 'merchant_name', secret: false},
    ],
};

const Field = ({label, hint, children}) => (
    <div>
        <label style={{display: 'block', fontSize: '.8rem', color: '#6b7280', marginBottom: 4}}>{label}</label>
        {children}
        {hint && <div style={{fontSize: '.75rem', color: '#9ca3af', marginTop: 4}}>{hint}</div>}
    </div>
);

// Boolean fields (currently just Hepsiburada's test_environment) render as an
// inline checkbox + label rather than Field's label-above-control layout,
// matching the Checkbox pattern used elsewhere in the onboarding wizard (see
// SettingsOnboardingStep.js's FlagRow).
const CheckboxField = ({id, label, hint, checked, disabled, onChange}) => (
    <div>
        <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
            <Checkbox inputId={id} checked={checked} disabled={disabled} onChange={onChange}/>
            <label htmlFor={id} style={{margin: 0, fontSize: '.85rem', color: '#374151'}}>{label}</label>
        </div>
        {hint && <div style={{fontSize: '.75rem', color: '#9ca3af', marginTop: 4, marginLeft: 26}}>{hint}</div>}
    </div>
);

// Selectable marketplace logo card. Visually mirrors the wizard's usage-type
// cards in Campaign/CampaignEdit.js: 2px border + tinted background + colored
// shadow when active. The neutral border/shadow and the hover state for the
// unselected look live in MarketplaceConnectStep.css (inline active styles
// below always win over those rules). Logo images aren't in the repo yet --
// /img/marketplaces/<key>.png is tried first, and onError swaps in a
// brand-colored initial badge so the card never shows a broken image.
const MarketplaceCard = ({marketplace, active, onSelect}) => {
    const [logoFailed, setLogoFailed] = useState(false);

    const onKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect();
        }
    };

    return (
        <div role="button" tabIndex={0} onClick={onSelect} onKeyDown={onKeyDown}
             className="mp-marketplace-card"
             style={{
                 position: 'relative', cursor: 'pointer', textAlign: 'left',
                 borderRadius: 12, padding: 14, width: 168,
                 transition: 'border-color .12s, box-shadow .12s, background .12s',
                 ...(active ? {
                     border: `2px solid ${marketplace.color}`,
                     background: `${marketplace.color}0d`,
                     boxShadow: `0 2px 10px ${marketplace.color}22`,
                 } : {}),
             }}>
            {active &&
                <i className="pi pi-check-circle"
                   style={{position: 'absolute', top: 10, right: 10, color: marketplace.color, fontSize: '1.1rem'}}/>}
            <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                {logoFailed
                    ? <span style={{
                        width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                        background: marketplace.color, color: '#fff', fontWeight: 700,
                        display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        {marketplace.initial}
                      </span>
                    : <img src={`/img/marketplaces/${marketplace.key}.png`} alt={marketplace.label}
                           onError={() => setLogoFailed(true)}
                           style={{width: 38, height: 38, borderRadius: 10, objectFit: 'contain', flexShrink: 0}}/>}
                <span style={{fontWeight: 700, color: '#111827'}}>{marketplace.label}</span>
            </div>
        </div>
    );
};

// Small connect form for the wizard's mp_connect step. Owns the marketplace
// choice itself (the wizard no longer picks it via a Data source dropdown).
// Exposes save() via ref so the wizard's "Next" button can trigger the connect
// call: resolves true on success, false on failure (never rejects), mirroring
// PriceCheckOnboardingStep.
//
// `lockedMarketplace` (optional): when set, the campaign's marketplace is
// already decided (see MarketplaceIntegrationStep.js) -- only that
// marketplace's card renders and the `marketplace` state is pinned to it, so
// there's no picker for the other marketplace. When omitted, both cards
// render and are selectable, as in the creation wizard (CampaignWizard.js
// mp_connect step), which is where the choice is actually made.
//
// `hideMarketplacePicker` (optional, default false): suppresses the picker
// section entirely (label + card(s)), regardless of lockedMarketplace.
// MarketplaceIntegrationStep passes true only once a campaign is connected,
// since its own consolidated panel already shows the same logo+name above
// this embedded step, and the locked single card here would just repeat it.
// CampaignWizard's unlocked usage never passes this prop, so its two-card
// picker (where the marketplace choice actually happens) is unaffected.
const MarketplaceConnectStep = forwardRef(({t, campaignId, lockedMarketplace, hideMarketplacePicker = false,
                                            onBeforeRedirect}, ref) => {
    const [marketplace, setMarketplace] = useState(lockedMarketplace || null);
    const [values, setValues] = useState({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    // While the browser is being sent to Amazon's consent page -- disables
    // the button so a double click can't fire two OAuth starts (each mints
    // its own single-use state) in the moment before the page navigates away.
    const [connecting, setConnecting] = useState(false);
    // Per-marketplace stored credential summary (merchant_id/username/merchant_name
    // + has_password), keyed by marketplace namespace. Credentials live on the
    // user (see product/marketplace_api/service_marketplace_campaign.py), not the
    // campaign, so this can prefill a namespace the user connected on a different
    // campaign too. Populated from GET marketplace_integration on mount.
    const [credentials, setCredentials] = useState({});
    // Amazon's country list for the marketplace_id dropdown: {marketplace_id,
    // iso_code, country}, one row per supported Amazon marketplace. Served by
    // GET marketplace_integration (see product/marketplace_api/service_marketplace_campaign.py
    // get_integration_summary) rather than duplicated here -- the backend's
    // marketplace_countries.py is the single source of truth for the list.
    const [amazonMarketplaces, setAmazonMarketplaces] = useState([]);
    // Whether the admin-only manual Amazon fields are revealed. Closed by
    // default: see MANUAL_AMAZON_FIELD_KEYS below for why.
    const [manualOpen, setManualOpen] = useState(false);

    // Admin-only fields: Hepsiburada's test_environment, and (now that the
    // Amazon app is published and self-service via OAuth is the normal path)
    // Amazon's seller_id/refresh_token -- the manual fallback kept ONLY for
    // support to connect an account without the seller present. Amazon's
    // marketplace_id used to be admin-only too (Change 2), but is a
    // customer-selectable country dropdown for everyone. Filtered here
    // (render list) rather than out of FIELDS itself so valuesFromStored --
    // keyed generically off FIELDS[marketplaceKey] -- still tracks/prefills
    // each field's stored state for admins. The backend mirrors this gate
    // independently (defense in depth -- see service_marketplace_campaign.py
    // connect's is_admin check; the OAuth callback path never accepts a
    // client-supplied seller_id/refresh_token at all -- see
    // service_amazon_oauth.py).
    const ADMIN_ONLY_FIELD_KEYS = ['test_environment', 'seller_id', 'refresh_token', 'merchant_name'];
    const allowedFields = (FIELDS[marketplace] || [])
        .filter(f => !ADMIN_ONLY_FIELD_KEYS.includes(f.key) || isAdmin());
    // Amazon's manual pair is admin-only AND folded away behind a disclosure,
    // so an admin's screen matches what a customer actually sees: a country and
    // a button. Leaving them inline made an admin's own screen look like the
    // customer's while asking for two values no customer is ever asked for --
    // which is exactly how it got mistaken for the customer flow. The fields
    // still work when opened; this only changes what is visible by default.
    // What an admin gets that a seller does not: the manual credential pair, and
    // the store-name override. The connected card above already tells a seller
    // which store is linked, so repeating it here as a field they cannot edit
    // was noise -- and the override itself is an admin action, not a step in
    // connecting. Folding all three away keeps the default screen identical for
    // both audiences.
    const ADMIN_DISCLOSURE_FIELD_KEYS = ['seller_id', 'refresh_token', 'merchant_name'];
    const isAdminDisclosureField = (f) =>
        marketplace === 'amazon' && ADMIN_DISCLOSURE_FIELD_KEYS.includes(f.key);
    const fields = allowedFields.filter(f => !isAdminDisclosureField(f));
    const manualFields = allowedFields.filter(isAdminDisclosureField);
    const hasStoredPassword = !!credentials[marketplace]?.has_password;
    // Whether this user already has a working Amazon connection (seller_id is
    // only ever stored once connect() has actually validated credentials --
    // see SAFE_CREDENTIAL_FIELDS in service_marketplace_campaign.py) -- drives
    // the "already connected" note next to the Connect with Amazon button.
    const hasAmazonConnection = !!credentials.amazon?.seller_id;

    // State keyed by a field's `optionsKey` (currently only Amazon's
    // marketplace_id -> amazonMarketplaces), so a future select field can
    // reuse this same rendering path instead of a special-cased one. Each
    // option's label is the flag glyph (derived from iso_code -- no image
    // assets) plus the country name, translated through the panel's own i18n
    // files rather than the backend's English names.
    const OPTIONS_SOURCES = {amazonMarketplaces};
    const optionsFor = (f) => (OPTIONS_SOURCES[f.optionsKey] || []).map(row => ({
        value: row.marketplace_id,
        iso: (row.iso_code || '').toLowerCase(),
        label: t(`wizard.mpConnect.amazonCountry.${row.iso_code}`),
    }));

    // Flag as an image, not an emoji. Regional indicator letters render as a
    // flag on macOS and iOS but as the bare country letters on Windows, which
    // ships no flag emoji font -- so most customers saw "TR", "DE" where the
    // design intended a flag. These SVGs live in public/img/flags (MIT, from
    // flag-icons); adding a marketplace means adding one file there.
    // alt is empty on purpose: the country name sits right beside it, and a
    // screen reader should not announce the country twice.
    const countryOption = (option) => (option ? (
        <span style={{display: 'flex', alignItems: 'center', gap: 8}}>
            <img src={`/img/flags/${option.iso}.svg`} alt="" width={20} height={15}
                 loading="lazy" style={{borderRadius: 2, flexShrink: 0}}/>
            {option.label}
        </span>
    ) : null);

    const set = (key, value) => setValues(prev => ({...prev, [key]: value}));

    // One field, rendered. Extracted so the main list and Amazon's folded-away
    // manual pair share exactly one rendering path rather than a copy each.
    const renderField = (f) => (
        f.type === 'checkbox'
            ? <CheckboxField key={f.key} id={f.key}
                             label={t(`wizard.mpConnect.field.${marketplace}.${f.key}`)}
                             hint={t(`wizard.mpConnect.fieldHint.${marketplace}.${f.key}`)}
                             checked={!!values[f.key]} disabled={saving}
                             onChange={(e) => set(f.key, e.checked)}/>
            : <Field key={f.key} label={t(`wizard.mpConnect.field.${marketplace}.${f.key}`)}
                     hint={t(`wizard.mpConnect.fieldHint.${marketplace}.${f.key}`)}>
                {f.type === 'select'
                    ? <Dropdown id={f.key} value={values[f.key] || f.defaultValue}
                                options={optionsFor(f)} optionLabel="label" optionValue="value"
                                itemTemplate={countryOption} valueTemplate={countryOption}
                                disabled={saving} style={{width: '100%'}}
                                onChange={(e) => set(f.key, e.value)}/>
                    : f.secret
                        ? <Password inputId={f.key} value={values[f.key] || ''} feedback={false} toggleMask
                                    disabled={saving} style={{width: '100%'}} inputStyle={{width: '100%'}}
                                    placeholder={hasStoredPassword ? t('wizard.mpConnect.passwordSavedPlaceholder') : undefined}
                                    onChange={(e) => set(f.key, e.target.value)}/>
                        : <InputText id={f.key} value={values[f.key] || ''}
                                     disabled={saving}
                                     style={{width: '100%'}} onChange={(e) => set(f.key, e.target.value)}/>}
              </Field>
    );

    // Non-secret stored fields only -- has_password is a flag, not a form value.
    // Reads generically off FIELDS[marketplaceKey] so each marketplace only
    // ever gets its own fields prefilled (e.g. Trendyol never picks up
    // Hepsiburada's merchant_name/test_environment, and vice versa). Checkbox
    // fields always get an explicit boolean default (false when there's no
    // stored value yet), even with no prior connection, so `values` -- and
    // therefore the credentials payload save() sends -- always carries a real
    // boolean rather than omitting the key (JSON.stringify drops `undefined`
    // keys, which would otherwise silently drop test_environment on a
    // brand-new connect).
    const valuesFromStored = (marketplaceKey, stored) => {
        const result = {};
        (FIELDS[marketplaceKey] || []).forEach(field => {
            if (field.secret) return;
            if (field.type === 'checkbox') {
                result[field.key] = !!(stored && stored[field.key]);
            } else {
                result[field.key] = (stored && stored[field.key]) || field.defaultValue || '';
            }
        });
        return result;
    };

    // Switching marketplace drops any credentials typed for the previous one --
    // the two marketplaces' fields don't line up (e.g. Hepsiburada's merchant_name).
    // Re-clicking the already-active card must be a no-op: the old SelectButton
    // was safe here because PrimeReact emits e.value = null on reselect and the
    // caller's `e.value &&` guard skipped it; the logo cards call onSelect
    // unconditionally, so this function must guard it itself.
    // The other marketplace may itself already have stored credentials (e.g. a
    // previous connection on another campaign) -- prefill from those, or clear
    // the form when there are none.
    const selectMarketplace = (value) => {
        if (value === marketplace) return;
        setMarketplace(value);
        setValues(valuesFromStored(value, credentials[value]));
        setError(null);
    };

    // Preselect the campaign's already-connected marketplace (if any) and
    // prefill its stored credentials, so reconnecting doesn't start blank.
    // When locked, the resolved marketplace is always lockedMarketplace itself
    // (ignoring whatever the fetched integration reports) -- there's no picker
    // to land on anything else.
    useEffect(() => {
        if (!campaignId) return;
        marketplaceProductService.getIntegration(campaignId).then(result => {
            const creds = result?.credentials || {};
            setCredentials(creds);
            setAmazonMarketplaces(result?.amazon_marketplaces || []);
            const resolved = lockedMarketplace || result?.marketplace;
            if (resolved) {
                setMarketplace(resolved);
                setValues(valuesFromStored(resolved, creds[resolved]));
            }
        }).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaignId]);

    // Backstop (Task 17 review fix): the mount effect above only runs once,
    // keyed on campaignId, so if `marketplace` state ever ends up out of sync
    // with a set `lockedMarketplace` -- e.g. this component rendered with the
    // lock still unresolved (undefined) and the user clicked the other card
    // before the caller's own load finished, or a future caller updates
    // lockedMarketplace after mount -- force it back. Keyed on
    // lockedMarketplace only, so it never fights the mount effect and is a
    // no-op whenever nothing is locked or state already agrees.
    useEffect(() => {
        if (lockedMarketplace && marketplace !== lockedMarketplace) {
            setMarketplace(lockedMarketplace);
            setValues(valuesFromStored(lockedMarketplace, credentials[lockedMarketplace]));
            setError(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lockedMarketplace]);

    // Self-service Amazon: sends the browser to Amazon's own consent page.
    // The backend mints a single-use state bound to (user, campaign,
    // marketplace_id) BEFORE this navigation happens (see amazon_oauth_start
    // / service_amazon_oauth.start) -- this call is what creates that state,
    // not merely a redirect helper. The return hash is remembered in
    // sessionStorage because this is a full top-level navigation: the whole
    // React tree (this dialog/step included) is gone by the time the seller
    // is back, and AmazonOAuthCallback.js -- a page that renders standalone,
    // outside the router entirely (see index.js) -- is what reads it back
    // once Amazon redirects to /amazon/callback.
    const connectWithAmazon = () => {
        if (!campaignId) return;
        const marketplaceId = values.marketplace_id || 'A33AVAJ2PDY3EV';
        setError(null);
        setConnecting(true);
        marketplaceProductService.startAmazonOAuth(campaignId, marketplaceId).then(result => {
            if (!result || !result.consent_url) {
                setError(t('api.detail.500'));
                setConnecting(false);
                return;
            }
            try {
                sessionStorage.setItem(AMAZON_OAUTH_RETURN_HASH_KEY, window.location.hash || '#/campaign');
            } catch (e) { /* private browsing / storage disabled -- the callback page falls back to the campaign list */ }
            // Let whoever is hosting this step record how to reopen itself. The
            // hash above only gets the browser back to the right screen; the
            // wizard is a modal whose step is not in the URL, so without this
            // the seller returns to the campaign list with the wizard shut.
            if (onBeforeRedirect) onBeforeRedirect();
            window.location.href = result.consent_url;
        }).catch(err => {
            const data = err && err.response && err.response.data;
            setError((data && data.error) || t('api.detail.500'));
            setConnecting(false);
        });
    };

    const save = () => {
        if (!marketplace) {
            setError(t('wizard.mpConnect.marketplacePicker.validation'));
            return Promise.resolve(false);
        }
        if (!campaignId) return Promise.resolve(false);
        // Self-service Amazon (non-admin): there is no manual credentials
        // form to submit -- "Connect with Amazon" above already completes the
        // connect server-side via OAuth (or is in flight). If it already
        // succeeded, credentials.amazon carries the seller_id connect()
        // stored, and "Next" simply moves on; otherwise the seller hasn't
        // connected yet and "Next" must not silently continue.
        if (marketplace === 'amazon' && !isAdmin()) {
            if (credentials.amazon && credentials.amazon.seller_id) {
                return Promise.resolve(true);
            }
            setError(t('wizard.mpConnect.amazonConnectRequired'));
            return Promise.resolve(false);
        }
        // Trendyol's store name is required (it labels the owner row; Trendyol's
        // API never returns it). Fail fast client-side; the backend enforces it too.
        if (marketplace === 'trendyol' && !(values.merchant_name || '').trim()) {
            setError(t('wizard.mpConnect.storeNameRequired'));
            return Promise.resolve(false);
        }
        setError(null);
        setSaving(true);
        return marketplaceProductService.connect(campaignId, marketplace, values).then(() => {
            return true;
        }).catch(err => {
            const data = err && err.response && err.response.data;
            if (data && data.error_code === 'invalid_store_name') {
                const suggestion = data.details && data.details.suggested_store_name;
                setError(suggestion
                    ? t('wizard.mpConnect.invalidStoreNameSuggest', {name: suggestion})
                    : t('wizard.mpConnect.invalidStoreName'));
            } else if (data && data.error_code === 'marketplace_not_participating') {
                // Backend message is English-only (details.chosen_country /
                // details.sold_countries); the panel renders its own
                // translated version instead, from the iso codes it also
                // carries, via the same amazonCountry i18n table the
                // dropdown itself uses.
                const details = data.details || {};
                const country = details.chosen_iso_code
                    ? t(`wizard.mpConnect.amazonCountry.${details.chosen_iso_code}`)
                    : details.chosen_country;
                let message = t('wizard.mpConnect.amazonNotParticipating', {country});
                const soldCountries = (details.sold_iso_codes || [])
                    .map(iso => t(`wizard.mpConnect.amazonCountry.${iso}`));
                if (soldCountries.length) {
                    message += ' ' + t('wizard.mpConnect.amazonNotParticipatingSells',
                        {countries: soldCountries.join(', ')});
                }
                setError(message);
            } else {
                setError((data && (data.error || data.detail)) || t('api.detail.500'));
            }
            return false;
        }).finally(() => setSaving(false));
    };

    // Expose save() to the wizard so its "Next" button can persist this step.
    useImperativeHandle(ref, () => ({save}));

    // Locked: only the campaign's marketplace card renders (selected, display
    // only -- selectMarketplace is already a no-op on the active card, so no
    // extra guard is needed to keep it inert).
    const visibleMarketplaces = lockedMarketplace
        ? MARKETPLACES.filter(m => m.key === lockedMarketplace)
        : MARKETPLACES;

    return (
        <div style={{maxWidth: 480}}>
            {!hideMarketplacePicker &&
                <div style={{marginBottom: 16}}>
                    <label style={{display: 'block', fontSize: '.8rem', color: '#6b7280', marginBottom: 6}}>
                        {t('wizard.mpConnect.marketplacePicker.label')}
                    </label>
                    <div style={{display: 'flex', gap: 12}}>
                        {visibleMarketplaces.map(m => (
                            <MarketplaceCard key={m.key} marketplace={m} active={marketplace === m.key}
                                              onSelect={() => selectMarketplace(m.key)}/>
                        ))}
                    </div>
                </div>}
            {error && <div style={{marginBottom: 14}}><Message severity="error" text={error}/></div>}
            {marketplace &&
                <div style={{border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, background: '#fff'}}>
                    <div style={{display: 'flex', flexDirection: 'column', gap: 14}}>
                        {fields.map(renderField)}
                    </div>
                    {/* Self-service: sends the browser to Amazon's own consent page --
                        see connectWithAmazon. This is the whole flow for a customer; an
                        admin gets the same screen, with the manual fallback folded away
                        below it. */}
                    {marketplace === 'amazon' &&
                        <div style={{marginTop: 14}}>
                            {hasAmazonConnection &&
                                <div style={{marginBottom: 10, fontSize: '.8rem', color: '#059669',
                                            display: 'flex', alignItems: 'center', gap: 6}}>
                                    <i className="pi pi-check-circle"/>
                                    {t('wizard.mpConnect.amazonConnected')}
                                </div>}
                            <Button type="button" label={t('wizard.mpConnect.amazonConnectButton')}
                                    icon="pi pi-external-link" iconPos="right"
                                    onClick={connectWithAmazon} disabled={connecting || saving}
                                    loading={connecting} style={{width: '100%'}}/>
                            <div style={{fontSize: '.75rem', color: '#9ca3af', marginTop: 6}}>
                                {t('wizard.mpConnect.amazonConnectHint')}
                            </div>
                            {/* Support's escape hatch: connecting an account without the
                                seller present. Admin-only (manualFields is already empty
                                for everyone else) and closed by default, so this screen
                                reads the same way it does for a customer. */}
                            {manualFields.length > 0 &&
                                <div style={{marginTop: 14, paddingTop: 12, borderTop: '1px solid #f0f1f3'}}>
                                    <Button type="button" link
                                            label={t(manualOpen ? 'wizard.mpConnect.amazonManualHide'
                                                                : 'wizard.mpConnect.amazonManualShow')}
                                            icon={manualOpen ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
                                            iconPos="right" onClick={() => setManualOpen(o => !o)}
                                            style={{padding: 0, fontSize: '.8rem'}}/>
                                    {manualOpen &&
                                        <div style={{display: 'flex', flexDirection: 'column', gap: 14, marginTop: 12}}>
                                            <div style={{fontSize: '.75rem', color: '#9ca3af'}}>
                                                {t('wizard.mpConnect.amazonManualHint')}
                                            </div>
                                            {manualFields.map(renderField)}
                                        </div>}
                                </div>}
                        </div>}
                </div>}
        </div>
    );
});

export default MarketplaceConnectStep;
